import * as THREE from 'three/webgpu';
import { juiceFor, RECOIL_DECAY, deathSpinShrink } from '../PlayerVisuals.js';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { clone as cloneSkinned } from 'three/addons/utils/SkeletonUtils.js';
import { CHARACTERS } from '@chaos/protocol';
import { createAssetLoader } from '../assets/AssetLoader.js';
import { CHARACTER_ASSET_REVISION, SKINNED_CHARACTER_IDS } from '../assets/revision.js';
import { isTouch } from '../config.js';

/**
 * Meshy-authored characters: one skinned GLB per roster id, twelve clips inside.
 *
 * ── Why this is a drop-in and not a new code path ─────────────────────────
 *
 * Everything in the client reaches a body through two things: the group that
 * `createCharacterModel` returns, and `group.userData.driver`. The local player,
 * every remote player and the mockup all go through that pair and nothing else.
 * So a skinned character only has to produce the same pair — the driver here
 * implements exactly the surface `proc/pose.js` exposes (`update`, `cast`,
 * `dash`, `hit`, `die`, `revive`, `dead`) and every call site works unchanged.
 *
 * The alternative — teaching each call site about two kinds of body — would put
 * a conditional in the render loop, the remote-player pool and the HUD, and one
 * of them would eventually be missed.
 *
 * ── Clips are named for the GAME, not for Meshy ───────────────────────────
 *
 * `tools/meshy/character.mjs` writes each clip under the state it serves —
 * `cast_sphere`, not `Charged_Ground_Slam`. That keeps Meshy's catalogue an
 * implementation detail of the pipeline: swapping which library animation backs
 * `cast_sphere` is a change to one JSON file, not to this module.
 *
 * ── Casts are RETIMED, because a fixed clip would lie ─────────────────────
 *
 * Meshy returns clips of a fixed length. `pose.js` is explicit that the chamber
 * phase must last exactly `ability.windupMs`, because the wind-up is the
 * reactable half of a cast — Fault Line's 600ms coil has to dwarf the basic's
 * 90ms flick or the animation is telling the player something the sim does not
 * agree with. So the action's `timeScale` is set per cast from the ability's own
 * numbers rather than played at its authored rate.
 */



/**
 * The procedural body's coordinate convention, which authored models must join.
 *
 * `assemble.js` puts the leg pivot at `-1 + LEG_LEN` and reaches `LEG_LEN`
 * (0.5) down from it, so a procedural character's FEET ARE AT y = -1 and its
 * origin is its middle. An M-torso head tops out near +1.12, giving ~2.1 of
 * body. A Meshy export instead stands 1.7 tall with its feet at y = 0.
 *
 * Dropped in raw that is a character both a fifth too short and a full unit off
 * the floor — and since ability VFX anchor to `localPlayer.renderPos`, the SIM
 * position rather than the mesh, the same unit made every effect look detached
 * from the body it belonged to. One mismatch, three symptoms.
 */
const PROC_FEET_Y = -1;
const PROC_HEIGHT = 2.1;

/** The fade used when locomotion takes the body back. */
const CROSSFADE = 0.18;

/**
 * Clips that legitimately END somewhere other than where they started, and so
 * keep their root rotation when the rest is cancelled.
 *
 * `defeated` is the whole list: ending 99deg over is a body falling down, which
 * is the point of the clip. Everything else returns to its own heading, and the
 * sim owns facing.
 */
const KEEPS_ROOT_ROTATION = new Set(['defeated']);

/** hit.kind -> the clip that shape animates as. Mirrors pose.js's ARCHETYPES. */
const CAST_CLIP = {
  melee_cone: 'cast_melee_cone',
  sphere: 'cast_sphere',
  dash_line: 'cast_dash_line',
  projectile: 'cast_projectile',
  // A basic-attack bullet leaves the same hand a shell does.
  shot: 'cast_projectile',
  ground_zone: 'cast_ground_zone',
  self: 'cast_self',
};

/** Parsed GLTF per character id. Populated by `preloadSkinned`. */


/**
 * Which characters have a skinned model available.
 *
 * Deliberately explicit rather than "try to fetch and see": a miss would be a
 * 404 that the dev server answers with index.html, and a GLTF parse error deep
 * in a match is a worse failure than never taking this path. The list is
 * written by `tools/meshy/character.mjs install`, beside the files it names.
 */
export const SKINNED_IDS = SKINNED_CHARACTER_IDS;
const loader = new GLTFLoader();
const cache = createAssetLoader({
  ids: SKINNED_IDS,
  revision: CHARACTER_ASSET_REVISION,
  load: async (url) => {
    const gltf = await loader.loadAsync(url);
    conditionAsset(gltf);
    return gltf;
  },
});
export const skinnedLoadStatus = () => cache.status();

/** Read-only view of a model's conditioned clips, for `tools/probe.js`. */
export function skinnedClips(id) {
  return cache.get(id)?.animations ?? [];
}

/**
 * The track names `makeInPlace` rewrites, so a reader can find the same two.
 *
 * Exported rather than letting the caller match on /Hips/: the root bone name
 * is the rig's, not ours, and a model whose root is called something else would
 * silently report nothing while the cancelling still ran.
 */
export function rootTrackNames(id) {
  const gltf = cache.get(id);
  const bone = gltf ? findRootBone(gltf.scene) : null;
  return bone
    ? { position: `${bone.name}.position`, quaternion: `${bone.name}.quaternion` }
    : { position: null, quaternion: null };
}

export function hasSkinned(id) {
  return cache.has(id);
}

/**
 * Fetch only requested roster models; in-flight, ready and failed requests
 * are deduplicated for this page. The no-argument tool API loads the full set.
 *
 * Returns the ids that actually loaded, so the caller can report a partial
 * result instead of discovering it one character at a time.
 */
export async function preloadSkinned(ids = SKINNED_IDS) {
  const unique = [...new Set(ids)].filter(id => SKINNED_IDS.includes(id));
  const results = await Promise.all(unique.map(id => cache.request(id)));
  return unique.filter((id, i) => results[i] !== null);
}

/**
 * Fit the asset to the game's convention and make its clips in-place. Runs once
 * per model at preload, not per instance: `createCharacterModel` is called from
 * the remote-player pool mid-match, and that is not the place to be rewriting
 * keyframes.
 */
function conditionAsset(gltf) {
  gltf.scene.updateMatrixWorld(true);
  // setFromObject, not raw geometry bounds: the bones live under an Armature
  // scaled 0.01 (they are authored in centimetres), so anything that skips the
  // node matrices measures this character in the wrong unit.
  const box = new THREE.Box3().setFromObject(gltf.scene);
  const height = box.max.y - box.min.y;
  gltf.userData.fit =
    height > 0
      ? { scale: PROC_HEIGHT / height, offsetY: PROC_FEET_Y - box.min.y * (PROC_HEIGHT / height) }
      : { scale: 1, offsetY: 0 };

  const rootBone = findRootBone(gltf.scene);
  if (rootBone) for (const clip of gltf.animations) makeInPlace(clip, rootBone.name);
  if (isTouch) useStandardMaterials(gltf.scene);
}

/**
 * The Meshy exports ask for KHR_materials_specular (x2) and KHR_materials_ior,
 * which makes GLTFLoader build a MeshPhysicalMaterial: the most expensive lit
 * shader three has, once per body on screen. On a phone that buys nothing a
 * player can see. The emissive map is the albedo at full strength, so these
 * bodies are close to self-lit and the extra specular lobe is a faint
 * highlight. Touch devices get the standard model with the same maps. Done
 * once on the cached asset, so every clone inherits it.
 */
function useStandardMaterials(scene) {
  // One conversion per material, not per mesh: parts that shared a material
  // keep sharing its replacement.
  const converted = new Map();
  scene.traverse((o) => {
    if (!o.isMesh || !o.material?.isMeshPhysicalMaterial) return;
    const physical = o.material;
    let standard = converted.get(physical);
    if (!standard) {
      standard = new THREE.MeshStandardMaterial().copy(physical);
      standard.name = physical.name;
      converted.set(physical, standard);
    }
    o.material = standard;
  });
  // Textures are shared with the new materials; dispose() leaves them alone.
  for (const physical of converted.keys()) physical.dispose();
}

function findRootBone(scene) {
  let found = null;
  scene.traverse((o) => {
    if (!found && o.isBone && !o.parent?.isBone) found = o;
  });
  return found;
}

/**
 * Cancel a clip's ROOT MOTION — the net travel and turn it applies to the whole
 * body — while leaving the motion inside the clip untouched.
 *
 * The sim is authoritative for position and facing: it places the body every
 * frame from `pose.x/z` and turns it by `pose.yaw`. A clip that also moves its
 * own root is a second opinion, and the measurements say two clips hold a loud
 * one. `dash` travels 297cm — the character rolled three metres clear of where
 * the server said the player was. `cast_projectile` ends 124deg from where it
 * started, and `clampWhenFinished` then HOLDS that, so the body stood facing
 * sideways after every throw until the next crossfade.
 *
 * Deleting the root tracks outright is the usual shortcut and it is wrong here:
 * `Hips` is the only root this rig has, so dropping its tracks would also drop
 * the hip sway that makes a run read as a run. Removing only the DRIFT — the
 * first-to-last delta, taken out on a linear ramp — fixes the two offenders and
 * is a no-op on the clips already in place (`run` measures 0.00 travel, 0deg).
 *
 * `defeated` keeps its rotation: ending 99deg over is a body falling down, which
 * is the entire point of the clip. Its travel still goes, so the corpse stays
 * where the player died.
 */
function makeInPlace(clip, rootName) {
  for (const track of clip.tracks) {
    const times = track.times;
    const v = track.values;
    const n = times.length;
    if (n < 2) continue;
    const span = times[n - 1] - times[0];
    if (span <= 0) continue;

    if (track.name === `${rootName}.position`) {
      // Horizontal only. Vertical is authored weight — the crouch in a cast,
      // the collapse in a death — and the sim's own Y rides the outer group.
      const dx = v[(n - 1) * 3] - v[0];
      const dz = v[(n - 1) * 3 + 2] - v[2];
      // The DRIFT only. Subtracting the start value as well would re-centre the
      // hips on the armature origin, which is the same thing on this rig — its
      // Hips rest at x/z 0 — and a silent sideways shove on any rig where they
      // do not, with each clip shoved by its own amount so they disagree across
      // a crossfade. `probe.js`'s travel metric reads 0 either way, so the
      // measurement this change is justified by cannot see the difference.
      for (let k = 0; k < n; k++) {
        const u = (times[k] - times[0]) / span;
        v[k * 3] -= dx * u;
        v[k * 3 + 2] -= dz * u;
      }
    } else if (track.name === `${rootName}.quaternion` && !KEEPS_ROOT_ROTATION.has(clip.name)) {
      const q0 = new THREE.Quaternion(v[0], v[1], v[2], v[3]);
      const q1 = new THREE.Quaternion(v[(n - 1) * 4], v[(n - 1) * 4 + 1], v[(n - 1) * 4 + 2], v[(n - 1) * 4 + 3]);
      // Quaternions double-cover: q and -q are the same rotation, and taking
      // the delta without this can read a small turn as an almost full one.
      if (q0.dot(q1) < 0) q1.set(-q1.x, -q1.y, -q1.z, -q1.w);
      const drift = q1.clone().multiply(q0.clone().invert());
      const step = new THREE.Quaternion();
      const q = new THREE.Quaternion();
      for (let k = 0; k < n; k++) {
        const u = (times[k] - times[0]) / span;
        step.identity().slerp(drift, u).invert();
        q.set(v[k * 4], v[k * 4 + 1], v[k * 4 + 2], v[k * 4 + 3]);
        q.premultiply(step);
        v[k * 4] = q.x;
        v[k * 4 + 1] = q.y;
        v[k * 4 + 2] = q.z;
        v[k * 4 + 3] = q.w;
      }
    }
  }
}

/**
 * Build one instance. Synchronous, and requires `preloadSkinned` to have run —
 * `createCharacterModel` is called mid-match from the remote-player pool and
 * cannot await.
 */
export function createSkinnedModel(characterId, { pooled = false } = {}) {
  const gltf = cache.get(characterId);
  if (!gltf) return null;

  // SkeletonUtils.clone, not Object3D.clone: a plain clone shares the skeleton,
  // so every player on the board would play the last animation any of them
  // started.
  /**
   * An OUTER group carries the game's transform; the asset's own root is left
   * exactly as authored.
   *
   * Setting `rotation.order = 'YXZ'` directly on the GLTF root is what broke
   * the first version: the order change keeps the x/y/z values and changes how
   * they compose, so a root with any baked rotation — which a Blender-derived
   * export has — silently becomes a different orientation. The character came
   * out lying on its face while the standalone viewer, which never touched the
   * order, showed it standing.
   *
   * The procedural rig can set the order on itself because its rotation is
   * zero. An authored asset cannot, so the two concerns get two objects.
   */
  const inner = cloneSkinned(gltf.scene);
  const root = new THREE.Group();
  root.rotation.order = 'YXZ';
  root.add(inner);

  // Measured at preload; see `conditionAsset`.
  const fit = gltf.userData.fit ?? { scale: 1, offsetY: 0 };
  inner.scale.multiplyScalar(fit.scale);
  inner.position.y += fit.offsetY;

  /**
   * The `userData` contract a body owes the rest of the client.
   *
   * `characterId` and `driver` are the obvious two. `colorHex` is the one that
   * is easy to miss and fails loudly a frame later: the remote nameplate paints
   * its edge with it and the status aura tints from it, so a body without it
   * throws once per frame from inside the render loop.
   *
   * `hullMeshes` is empty on purpose rather than absent — the outline on this
   * path comes from the material, not from inverted-hull geometry, and every
   * reader guards with `?? []`, so an explicit empty array says "considered"
   * where a missing key says "forgotten".
   */
  root.userData.characterId = characterId;
  root.userData.colorHex = CHARACTERS[characterId]?.colorHex ?? 0xffffff;
  root.userData.hullMeshes = [];
  root.userData.skinned = true;
  /**
   * Where a crown sits above the head. `assemble.js` sets this and
   * `PlayerVisuals.js` reads it behind a `??`, so a body without it does not
   * fail — the crown just lands wherever the procedural default put it, on a
   * body of a different height. Derived from the fitted bounds rather than
   * guessed, so it moves with the model.
   */
  root.userData.crownY = PROC_FEET_Y + PROC_HEIGHT + 0.14;

  inner.traverse((o) => {
    if (!o.isMesh && !o.isSkinnedMesh) return;
    o.castShadow = !pooled;
    o.frustumCulled = false; // skinned bounds are bind-pose and cull too early

    /**
     * A per-instance material, because three things in this client write to a
     * body's material and `SkeletonUtils.clone` hands every clone the SAME one.
     *
     *   - the hit flash writes a white mix on the body that was struck,
     *   - `setCharacterShimmer` fades a body that has gone invisible,
     *   - teardown disposes it when that player leaves.
     *
     * Shared, each of those reaches every other body of the character and the
     * cached asset behind them: one player's flash whitens the whole roster,
     * and one player leaving disposes a material everyone else is still drawn
     * with. `clone()` copies the material and KEEPS the texture reference, so
     * the 200KB albedo is still loaded once.
     */
    o.material = Array.isArray(o.material)
      ? o.material.map((m) => m.clone())
      : o.material.clone();
    // Teardown reads this: the material is ours to dispose, the map is not.
    o.userData.fromAsset = true;
  });

  // The mixer binds to the asset root, since that is what the clips' tracks name.
  const mixer = new THREE.AnimationMixer(inner);

  /**
   * Actions are built on first use, not up front.
   *
   * `AnimationAction` allocates one interpolant per track, and these models
   * carry twelve clips of ~72 channels — so building all twelve eagerly is
   * ~864 interpolants per body, allocated synchronously inside the remote-player
   * pool on the frame a player first appears. Seven opponents arriving in one
   * snapshot is ~6,000 allocations in a single frame, at exactly the moment the
   * fight starts.
   *
   * A body reaches at most a handful of its clips in a round, and each arrives
   * on a different frame, so this spreads the cost out and never pays for a clip
   * nobody plays.
   */
  const clips = new Map(gltf.animations.map((c) => [c.name, c]));
  const actions = new Map();
  const actionFor = (name) => {
    let action = actions.get(name);
    if (action === undefined) {
      const clip = clips.get(name);
      if (clip === undefined) return null;
      action = mixer.clipAction(clip);
      action.clampWhenFinished = true;
      actions.set(name, action);
    }
    return action;
  };

  root.userData.driver = createSkinnedDriver(inner, mixer, actionFor, root, characterId);
  return root;
}


/**
 * `root` is the asset's own root (animated by the mixer); `outer` is the game's
 * transform group around it, which is where the recoil pitch and the death
 * spin-shrink go — the asset's baked rotation must never be written to.
 */
function createSkinnedDriver(root, mixer, actionFor, outer = root, characterId = null) {
  const juice = juiceFor(characterId);
  /** 1 on the strike frame of a cast, relaxing at RECOIL_DECAY. */
  let recoil = 0;
  /** When the current cast's strike lands (ms), 0 when none is pending. */
  let kickAt = 0;
  /** Seconds since death. */
  let deadT = 0;
  let current = null;
  let dead = false;
  /** A one-shot in flight: nothing may take the body until it ends. */
  let holdUntil = 0;

  function play(name, { loop = false, fade = CROSSFADE } = {}) {
    const next = actionFor(name);
    if (!next || next === current) {
      if (next && !loop) {
        // Re-firing the same one-shot restarts it rather than being ignored.
        // `oneShot` sets the rate straight after, which is the only caller that
        // wants one other than 1.
        next.reset().play();
        next.timeScale = 1;
      }
      return next ?? null;
    }
    next.reset();
    next.timeScale = 1;
    next.setLoop(loop ? THREE.LoopRepeat : THREE.LoopOnce, loop ? Infinity : 1);
    next.fadeIn(fade).play();
    if (current) current.fadeOut(fade);
    current = next;
    return next;
  }

  function update(now, dt, { vx = 0, vz = 0, grounded = true } = {}) {
    mixer.update(dt);
    if (dead) {
      // The defeated clip plays inside a group that spins and shrinks to
      // nothing; fx.defeat's burst takes over from there.
      deadT += dt;
      deathSpinShrink(outer, deadT, dt);
      return;
    }
    // The strike frame kicks the whole body back; the kick relaxes at
    // RECOIL_DECAY, weighted by archetype (the tank barely moves).
    if (kickAt && now >= kickAt) {
      kickAt = 0;
      recoil = 1;
    }
    if (recoil > 0.005) {
      recoil *= Math.exp(-RECOIL_DECAY * dt);
      outer.rotation.x = -0.2 * recoil * juice.recoil;
    } else if (recoil !== 0) {
      recoil = 0;
      outer.rotation.x = 0;
    }
    if (now < holdUntil) return;

    // Airborne first: `jump` is generated, merged and shipped in every model, and
    // choosing from horizontal speed alone left it permanently unplayed — a body
    // in the air kept running on nothing while the procedural driver posed it.
    if (!grounded) {
      play('jump', { loop: true, fade: 0.12 });
      return;
    }

    // Locomotion is the resting state: whatever else finished, come back here.
    const speed = Math.hypot(vx, vz);
    play(speed > 0.4 ? 'run' : 'idle', { loop: true });
  }

  /**
   * `oneShot` owns the body for its own duration, expressed in ms so it can be
   * compared against the sim's numbers directly.
   */
  function oneShot(now, name, durationMs, fade = 0.1) {
    const action = play(name, { loop: false, fade });
    if (!action) return;
    const clipMs = action.getClip().duration * 1000;
    // Fit the clip to the window the sim gives us, rather than the other way
    // round. A clip that outlives its own gameplay window is the animation
    // still swinging after the damage has landed.
    action.timeScale = clipMs > 0 ? clipMs / Math.max(80, durationMs) : 1;
    holdUntil = now + durationMs;
  }

  return {
    update,

    /**
     * Timing comes from the ability, exactly as the procedural driver does it:
     * the windup is the reactable half and the recovery is the committed one.
     */
    cast(now, ability) {
      if (!ability || dead) return;
      const name = CAST_CLIP[ability.hit?.kind] ?? CAST_CLIP.melee_cone;
      const windup = Math.max(0, ability.windupMs ?? 0);
      const recover = Math.max(160, ability.recoveryMs ?? 0);
      oneShot(now, name, windup + recover);
      kickAt = now + windup;
    },

    dash(now, durationMs = 220) {
      if (dead) return;
      oneShot(now, 'dash', Math.max(60, durationMs), 0.06);
    },

    hit(now) {
      if (dead) return;
      // Short and interruptible: a flinch that outlasts the next input reads as
      // a stun, and this game has a separate mechanic for that.
      oneShot(now, 'flinch', 260, 0.05);
    },

    die() {
      if (dead) return;
      dead = true;
      deadT = 0;
      kickAt = 0;
      play('defeated', { loop: false, fade: 0.12 });
    },

    revive() {
      if (!dead) return;
      dead = false;
      holdUntil = 0;
      recoil = 0;
      outer.scale.set(1, 1, 1);
      outer.rotation.x = 0;
      play('idle', { loop: true, fade: 0.12 });
    },

    get dead() {
      return dead;
    },

    /** Released with the body; a mixer holds references to every clip it built. */
    dispose() {
      mixer.stopAllAction();
      mixer.uncacheRoot(root);
    },
  };
}
